<template>
    <div
        data-next-gen="list-item"
        class="p-4 space-y-2"
        v-bind="$attrs"
    >
        <slot name="feature-top" />
        <div class="flex items-center space-x-4 w-full">
            <slot
                v-if="$slots['start-item'] || startItem"
                name="start-item"
            >
                <template v-if="startItem === 'time'">
                    <div class="w-12 shrink-0 flex flex-col items-center space-y-0.5 pt-[7px]">
                        <span class="content-md-ux-semibold text-brand-base">{{ time }}</span>
                        <span class="content-xs-ux-medium text-brand-base mb-0.5">{{ amPm }}</span>
                    </div>
                </template>
                <template v-else-if="startItem === 'date'">
                    <div class="w-12 shrink-0 flex flex-col items-center space-y-0.5 pt-[7px]">
                        <span class="content-xs-ux-medium text-brand-base mb-0.5">{{ dayOfWeek }}</span>
                        <span class="content-md-ux-semibold text-brand-base">{{ dayOfMonth }}</span>
                    </div>
                </template>
                <template v-else-if="startItem === 'icon'">
                    <DIcon
                        :icon="startIcon"
                        size="w-5 h-5"
                        class="shape-brand-base shrink-0"
                    />
                </template>
            </slot>
            <div class="grow space-y-1">
                <slot name="eyebrow" />
                <div
                    v-if="$slots['title'] || title"
                    class="content-md-ux-medium text-neutral-base mb-1"
                >
                    <slot name="title">
                        {{ title }}
                    </slot>
                </div>
                <div
                    v-if="$slots['description'] || description"
                    class="content-sm-ux-regular text-neutral-extra-subtle"
                    :class="{ 'line-clamp-1': shouldTruncateDescription }"
                >
                    <slot name="description">
                        {{ description }}
                    </slot>
                </div>
                <div
                    v-if="subtext || moreSubtext"
                    class="flex items-center w-full mt-3"
                >
                    <span
                        v-if="subtext"
                        class="content-xs-ux-regular text-neutral-extra-subtle"
                    >{{ subtext }}</span>
                    <div
                        v-if="moreSubtext"
                        class="ml-auto rounded-full content-xs-ux-medium bg-(--shape-brand-subtle) text-brand-extra-strong py-1 px-1.5"
                    >
                        {{ moreSubtext }}
                    </div>
                </div>
            </div>
            <slot
                v-if="$slots['end-item'] || endItem"
                name="end-item"
            >
                <template v-if="endItem === 'icon'">
                    <DIcon
                        :icon="endIcon"
                        size="w-5 h-5"
                        class="shape-neutral-base shrink-0"
                    />
                </template>
                <template v-else-if="endItem === 'image'">
                    <div class="relative h-20 w-20 shrink-0">
                        <div
                            class="h-20 w-20 shrink-0 relative overflow-hidden rounded-image-default before:rounded-image-default "
                            :class="{
                                'before:rounded-image-default before:absolute before:inset-0 before:bg-(--surface-secondary) before:animate-pulse': !endImageLoaded,
                            }"
                        >
                            <img
                                :src="endImage"
                                :alt="title"
                                class="w-full relative object-cover shrink-0 h-full"
                                @load="endImageLoaded = true"
                            >
                        </div>
                    </div>
                </template>
            </slot>
        </div>
        <slot name="feature-bottom" />
    </div>
    <!-- TODO: Change padding to use mx-content-sm once supported -->
    <div
        v-if="bottomDivider"
        class="h-px bg-(--border-neutral-subtle)"
        :class="bottomDivider === 'inset' ? 'mx-4' : null"
    />
</template>

<script setup>
    import { DIcon } from '@digistorm/spark'
    import dayjs from '@/dayjs'

    // TODO: Move this component to spark?
    const props = defineProps({
        startItem: {
            type: String,
            validator(value) {
                return ['date', 'time', 'icon'].includes(value)
            },
        },
        startDate: [String, Object],
        startIcon: {
            type: String,
            description: 'Icon to display at the start of the item.',
            iconify: true,
        },
        endItem: {
            type: String,
            validator(value) {
                return ['icon', 'image'].includes(value)
            },
        },
        endIcon: {
            type: String,
            description: 'Icon to display at the end of the item.',
            iconify: true,
        },
        endImage: String,
        title: {
            type: String,
            default: null,
            description: 'Gets replaced by title slot if slot is used.',
        },
        description: {
            type: String,
            default: null,
            description: 'Gets replaced by description slot if slot is used.',
        },
        descriptionTruncate: {
            type: Boolean,
            default: false,
            description: 'Whether to force description to one line.',
        },
        subtext: String,
        moreSubtext: String,
        bottomDivider: {
            type: String,
            validator(value) {
                return ['full', 'inset'].includes(value)
            },
            default: 'full',
        },
    })

    const endImageLoaded = ref(false)

    const startDateParsed = computed(() => {
        return props.startDate
            ? dayjs(props.startDate)
            : null
    })

    // If no start date is provided, we show "all day"
    const time = computed(() => {
        return startDateParsed.value?.format('h:mm') ?? 'all'
    })

    const amPm = computed(() => {
        return startDateParsed.value?.format('a') ?? 'day'
    })

    const dayOfWeek = computed(() => {
        return startDateParsed.value?.format('ddd')
    })

    const dayOfMonth = computed(() => {
        return startDateParsed.value?.format('D')
    })

    const shouldTruncateDescription = computed(() => {
        // Always truncate if there's an end image, note that using the description slot means it needs to be handled by the user
        return props.descriptionTruncate
            || (props.description && props.endItem === 'image')
    })
</script>
